/**
 * PhoneInput — wrapper su `react-international-phone` allineato al design
 * v2 (border 1.5px, radius 12, font Inter, focus cyan).
 *
 * Note:
 *  - il selettore del paese mostra un dropdown che deve "uscire" dal box
 *    dell'input → niente `overflow-hidden` sul wrapper, e z-index alto
 *    quando aperto (lasciamo il default `position:absolute` della lib).
 *  - il border-radius "tagliato" sul lato destro/sinistro del bottone e
 *    dell'input è gestito a livello di sub-elementi per evitare il clip.
 */

import { useMemo } from 'react'
import { PhoneInput as IntlPhoneInput } from 'react-international-phone'
import 'react-international-phone/style.css'
import { cn } from '@/lib/utils'

type Props = {
  id?: string
  value: string
  onChange: (value: string) => void
  hasError?: boolean
  disabled?: boolean
  placeholder?: string
  autoFocus?: boolean
}

export function PhoneInput({
  id,
  value,
  onChange,
  hasError,
  disabled,
  placeholder,
  autoFocus,
}: Props) {
  const inputStyle = useMemo(
    () => ({
      width: '100%',
      height: '40px',
      border: 'none',
      background: 'transparent',
      color: 'hsl(var(--foreground))',
      fontSize: '15px',
      fontFamily: 'inherit',
      outline: 'none',
      paddingLeft: '12px',
      borderRadius: '0 10px 10px 0',
    }),
    [],
  )

  const buttonStyle = useMemo(
    () => ({
      height: '40px',
      border: 'none',
      background: 'transparent',
      borderRadius: '10px 0 0 10px',
      paddingLeft: '10px',
      paddingRight: '6px',
    }),
    [],
  )

  return (
    <div
      className={cn(
        'phone-input-wrap relative flex h-11 items-stretch rounded-xl border-[1.5px] bg-background transition-colors focus-within:outline-none',
        hasError
          ? 'border-destructive focus-within:border-destructive'
          : 'border-input focus-within:border-secondary',
        disabled && 'opacity-50',
      )}
    >
      <IntlPhoneInput
        defaultCountry="it"
        value={value}
        onChange={onChange}
        disabled={disabled}
        placeholder={placeholder}
        inputProps={{ id, autoFocus, autoComplete: 'tel' }}
        inputStyle={inputStyle}
        countrySelectorStyleProps={{
          buttonStyle,
          dropdownStyleProps: {
            style: {
              background: 'hsl(var(--card))',
              border: '1px solid hsl(var(--border))',
              borderRadius: '8px',
              color: 'hsl(var(--foreground))',
              boxShadow: '0 10px 30px -10px rgba(0,0,0,0.4)',
              zIndex: 60,
              top: 'calc(100% + 4px)',
              left: 0,
              maxHeight: '260px',
              overflowY: 'auto',
            },
          },
        }}
      />
    </div>
  )
}
